/**
 * Menu "⋯" de uma linha (campanha, conjunto ou anúncio).
 *
 * Cada item aparece só para quem tem a permissão — esconder, não desabilitar,
 * é o padrão do repo. "Duplicar" pede confirmação porque cria objetos reais
 * na conta (pausados, mas reais) e a Meta não tem "desfazer".
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Copy, ExternalLink, MoreHorizontal, Wallet } from "lucide-react";
import { linkGerenciador } from "@/lib/meta-ads-format";
import type { ObjetoTipo } from "./StatusToggle";

const ROTULO: Record<ObjetoTipo, string> = { campaign: "campanha", adset: "conjunto", ad: "anúncio" };

export function RowActionsMenu({
  objectType,
  objectId,
  nome,
  adAccountId,
  canManage,
  canActivate,
  contaBloqueada,
  onEditBudget,
  onDuplicated,
}: {
  objectType: ObjetoTipo;
  objectId: string;
  nome: string;
  adAccountId: string | null | undefined;
  canManage: boolean;
  canActivate: boolean;
  contaBloqueada?: boolean;
  /** Ausente para anúncio (não tem verba própria). */
  onEditBudget?: () => void;
  onDuplicated: () => void;
}) {
  const [confirmar, setConfirmar] = useState(false);

  const opts = {
    onSuccess: () => {
      toast.success(`Cópia criada pausada: "${nome} (cópia)".`);
      onDuplicated();
    },
    onError: (err: unknown) => toast.error(toUserFriendlyError(err)),
  };
  const dupCampanha = trpc.metaAds.campaigns_write.duplicate.useMutation(opts);
  const dupConjunto = trpc.metaAds.adsets_write.duplicate.useMutation(opts);
  const dupAnuncio = trpc.metaAds.ads_write.duplicate.useMutation(opts);
  const duplicando = dupCampanha.isPending || dupConjunto.isPending || dupAnuncio.isPending;

  const duplicar = () => {
    setConfirmar(false);
    if (objectType === "campaign") dupCampanha.mutate({ campaignId: objectId });
    else if (objectType === "adset") dupConjunto.mutate({ adsetId: objectId });
    else dupAnuncio.mutate({ adId: objectId });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Ações" disabled={duplicando}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canActivate && onEditBudget && (
            <DropdownMenuItem onClick={onEditBudget} disabled={contaBloqueada}>
              <Wallet className="mr-2 h-4 w-4" /> Editar verba diária
            </DropdownMenuItem>
          )}
          {canManage && (
            <DropdownMenuItem onClick={() => setConfirmar(true)} disabled={contaBloqueada}>
              <Copy className="mr-2 h-4 w-4" /> Duplicar
            </DropdownMenuItem>
          )}
          {(canActivate || canManage) && <DropdownMenuSeparator />}
          <DropdownMenuItem asChild>
            <a href={linkGerenciador(adAccountId, objectType, objectId)} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" /> Abrir no Gerenciador
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmar} onOpenChange={setConfirmar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicar {ROTULO[objectType]}?</AlertDialogTitle>
            <AlertDialogDescription>
              {objectType === "ad"
                ? "Cria uma cópia do anúncio, pausada, no mesmo conjunto."
                : `Cria uma cópia de "${nome}" com ${objectType === "campaign" ? "os conjuntos e anúncios" : "os anúncios"} dela — tudo pausado, com o sufixo "(cópia)". Nada entra no ar até você ativar.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={duplicar}>Duplicar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
